/// <reference path="Utilities/_reference.ts" />

module YVR.ItineraryMap {
	// Utilities
	import consts = YVR.Utilities.Map.consts;
	import gtm = YVR.Utilities.Gtm;
	import util = YVR.Utilities.Map;

	// Constants
	const ANALYTICS_CATEGORY = 'itinerary-map';

	// Global state
	const cache: any = {};
	const layers: any = {};
	const state = util.config.initialState;
	const templates = {
		map: null,
		popup: null
	};

	let levels;
	let map: L.Map;
	let steps: {
		index: number,
		marker: L.Marker,
		step: IItineraryStep
	}[] = [];


	/**
	 * Configuration
	 */
	const config = $.extend({}, util.config, {
		subcategories: [
			util.consts.SUBCATEGORIES.BAGGAGE,
			util.consts.SUBCATEGORIES.PUBLIC_TRANSPORTATION,
			util.consts.SUBCATEGORIES.SECURITY_CHECKPOINTS,
			util.consts.SUBCATEGORIES.WASHROOMS
		]
	});


	/**
	 * Initialise the entire mapping component.
	 */
	function init() {
		if ($('.yvr-itinerary-map').length) {
			initTemplates();

			util.getFullMapData(cache);
		}
	}

	/**
	 * Run through the map steps.
	 */
	export function runMap(data: IItinerary) {
		$('.yvr-itinerary-map').html(templates.map({}));

		// Reset steps
		steps = [];

		if ($('.yvr-map--itinerary').length) {
			initMap();

			// Add `notranslate` classes to the map pane.
			$('.yvr-map--itinerary').find('.leaflet-pane').addClass('notranslate');

			renderFloorLevel();
			renderFacilityMarkers(data);
			initEventBindings();
			initFloor();
		}
	}


	//#region INIT MAP


	/**
	 * Initialises the popup template.
	 */
	function initTemplates() {
		const popupSource = $('#yvr-map-popup-template').html();

		if (popupSource) {
			templates.popup = Handlebars.compile(popupSource);
		}

		const mapSource = $('#yvr-itinerary-map-template').html();

		if (mapSource) {
			templates.map = Handlebars.compile(mapSource);
		}
	}


	/**
	 * Initialise the Leaflet map with our default options.
	 */
	export function initMap() {
		$('.yvr-itinerary-map').html(templates.map({}));

		levels = $('.yvr-map--itinerary').data('maps-levels');

		const options: L.MapOptions = {
			attributionControl: false,
			center: L.latLng(config.center[0], config.center[1]),
			maxBounds: util.createBounds(config.center, config.radius),
			minZoom: config.zoom.min,
			maxZoom: config.zoom.max,
			zoom: config.zoom.initial,

			// Turn off the default zoom control.
			zoomControl: false
		};

		config.showNonPassengerAreas = $('.yvr-map--itinerary').data('maps-show-non-passenger-areas') !== undefined;

		map = L.map(document.getElementById('yvr-map'), options);

		// Add a new zoom control with our customized position.
		map.addControl(new (L as any).Control.Zoom(config.zoomControl));
	}


	/**
	 * Initialise the event bindings for our custom refiner and level controls.
	 */
	function initEventBindings() {
		onLevelChange();
		onZoomChange();
	}


	/**
	 * Set the current floor by clicking on a Leaflet layer control.
	 */
	function setFloor(floor) {
		const nthChild = parseInt(floor, 10) - 1;

		$(`.leaflet-control-layers-base input:nth(${nthChild})`).trigger('click');
	}


	/**
	 * Run a fake click on a the Leaflet layer control to select our default floor.
	 */
	function initFloor() {
		setFloor(state.level);
	}


	/**
	 * Render a level of rooms from GeoJSON.
	 */
	function renderFloorLevel() {
		const controlLayers = L.control.layers();
		const rooms = config.urls.rooms;

		map.addLayer(renderBaseLevelLayer(cache[rooms.BaseLayer]));
		map.addLayer(renderParkingLevelLayer(cache[rooms.ParkingLots]));
		map.addLayer(renderRoadsLevelLayer(cache[rooms.Roads]));

		// Once all data is loaded, create each of the layers.
		for (let i = 1; i < 5; i++) {
			let layer = renderFloorLevelLayer(cache[rooms[i]], i);

			// Add the layer as a base layer, so it can be switched using the Leaflet layer control.
			controlLayers.addBaseLayer(layer, levels[i]);
		}

		// Create a layer control with each level.
		map.addControl(controlLayers);

		renderGateLayer();
		renderGateAreasLayer();

		updateMap();
	}


	/**
	 * Run through the steps to update the map markers on state changes.
	 */
	function updateMap() {
		util.resetRoomStyle(map, state.terminals, config);

		for (const marker of config.subcategories) {
			renderFacilityLayer(marker, consts.PROPERTIES.SUBCATEGORY);
		}
	}


	//#endregion


	//#region LAYER HELPERS


	/**
	 * Create a GeoJSON layer using a set of default properties.
	 * @param data The GeoJSON data to create a layer with.
	 * @param props The properties to pass into the GeoJSON layer constructor.
	 */
	function createGeoJsonLayer(data: Object, props: Object): L.GeoJSON {
		const defaults = {
			interactive: false,
			fillOpacity: 1,
			keyboard: false,
			opacity: 1
		};

		const properties = $.extend({}, defaults, props);

		return (L as any).geoJSON(data, properties) as L.GeoJSON;
	}


	/**
	 * Add or remove layers based on map importance and current zoom.
	 * @param name The name prefix of the collection of layers to add or remove.
	 */
	function resetAllLayersOnImportance(name: string): void {
		map.on('zoomend', () => {
			let zoom = map.getZoom();
			zoom = Math.ceil(zoom);

			map.addLayer(layers[`${name}_15`]);

			if (zoom >= 16) {
				map.addLayer(layers[`${name}_16`]);
			} else {
				map.removeLayer(layers[`${name}_16`]);
			}

			if (zoom >= 17) {
				map.addLayer(layers[`${name}_17`]);
			} else {
				map.removeLayer(layers[`${name}_17`]);
			}

			if (zoom >= 18) {
				map.addLayer(layers[`${name}_18`]);
			} else {
				map.removeLayer(layers[`${name}_18`]);
			}
		});
	}


	/**
	 * Change the icon size of maps based on zoom level.
	 * @param name The name of the type of facility.
	 * @param markers The collection of facilities to change icon size of.
	 */
	function resetIconsOnZoom(name: string, markers: L.Marker[]): void {
		map.on('zoomend', () => {
			let zoom = map.getZoom();
			const size = util.getSizeOnZoom(zoom);

			for (let marker of markers) {
				const icon = L.icon({
					className: (marker as any).options.icon.options.className,
					iconUrl: (marker as any).options.icon.options.iconUrl,
					iconSize: [size, size],
					iconAnchor: [size / 2, size / 2]
				});

				marker.setIcon(icon);
			}
		});
	}


	//#endregion


	//#region RENDER LAYERS


	/**
	 * Adds a base layer of a level on our map with the given GeoJSON data.
	 * @param {Object} data             GeoJSON data.
	 */
	function renderBaseLevelLayer(data: Object): L.GeoJSON {
		return createGeoJsonLayer(data, {
			style(feature) {
				return {
					color: util.getFeatureColor(feature, state.terminals, config)
				};
			}
		});
	}


	/**
	 * Adds a base parking lot layer of a level on our map with the given GeoJSON data.
	 * @param {Object} data             GeoJSON data.
	 */
	function renderParkingLevelLayer(data: Object): L.GeoJSON {
		return createGeoJsonLayer(data, {
			style(feature) {
				return {
					color: util.config.colors.road
				};
			}
		});
	}


	/**
	 * Adds a base layer of a level on our map with the given GeoJSON data.
	 * @param {Object} data             GeoJSON data.
	 */
	function renderRoadsLevelLayer(data: Object): L.GeoJSON {
		return createGeoJsonLayer(data, {
			style(feature) {
				return util.config.styles.roads;
			}
		});
	}


	/**
	 * Adds a base layer of a level on our map with the given GeoJSON data.
	 * @param {Object} data             GeoJSON data.
	 * @param {Number} floorLevelNumber The level number of the GeoJSON data.
	 */
	function renderFloorLevelLayer(data, floorLevelNumber) {
		return createGeoJsonLayer(data, {
			filter(feature) {
				// Filter out any sectors not necessary for the map, such as the parkade.
				return !config.excludedSectors.includes(feature.properties.Sector) &&
					floorLevelNumber === parseInt(feature.properties.FloorLevel, 10);
			},
			style(feature) {
				return {
					color: util.getFeatureColor(feature, state.terminals, config)
				};
			}
		});
	}


	/**
	 * Adds the facility icons to a map layer as a marker cluster. We are using marker clusters
	 * here to make pairs of washrooms appear to be just one washroom, as to not clutter the zoomed
	 * out views.
	 * @param {String} name     The name of the facility.
	 * @param {String} property The feature property to filter on.
	 */
	function renderFacilityLayer(name: string, property: string) {
		util.removeLayer(map, layers[`${name}_15`]);
		util.removeLayer(map, layers[`${name}_16`]);
		util.removeLayer(map, layers[`${name}_17`]);
		util.removeLayer(map, layers[`${name}_18`]);

		// Create a facility icon to use for both the cluster group and individual icons.

		const markers: L.Marker[] = [];

		layers[`${name}_15`] = (L as any).layerGroup();
		layers[`${name}_16`] = (L as any).layerGroup();
		layers[`${name}_17`] = (L as any).layerGroup();
		layers[`${name}_18`] = (L as any).layerGroup();

		let zoom = map.getZoom();
		zoom = Math.ceil(zoom);
		const size = util.getSizeOnZoom(zoom);

		for (const feature of cache['locations']) {
			if (util.filterFeature(feature, state, property, name)) {
				const latlng = L.latLng(feature.Latitude, feature.Longitude);
				const icon = util.createFacilityIcon(name, size, feature);

				// Remove facility ID to not show page links for bathrooms, and baggage carousels
				if (name === util.consts.SUBCATEGORIES.BAGGAGE || name === util.consts.SUBCATEGORIES.WASHROOMS) {
					feature.FacilityId = null;
				}

				// SPECIAL CASE: Baggage carousels have their own icons.
				if (name === util.consts.SUBCATEGORIES.BAGGAGE && feature.PoiNameLocID) {
					const matches = feature.PoiNameLocID.match(util.consts.REGEX.CAROUSEL_NUMBER);
					feature.Picto = true;

					if (matches && matches.length && matches[1] > 0) {
						feature.StoreFront_4x3_Url = `${config.urls.icons}/baggage-carousels/${matches[1]}.svg`;
					} else {
						feature.StoreFront_4x3_Url = `${config.urls.icons}/${name}.svg`;
					}
				} else {
					feature.Picto = true;
					feature.StoreFront_4x3_Url = `${config.urls.icons}/${name}.svg`;
				}

				const marker = L.marker(latlng, { icon });

				markers.push(marker);

				if (9 <= feature.PoiMapImportance && feature.PoiMapImportance <= 10) {
					// If importance is 9/10 show zoomed out
					layers[`${name}_15`].addLayer(marker);
				} else if (7 <= feature.PoiMapImportance && feature.PoiMapImportance <= 8) {
					// If importance is 7/8, show zoomed out
					layers[`${name}_16`].addLayer(marker);
				} else if (3 <= feature.PoiMapImportance && feature.PoiMapImportance <= 6) {
					// If importance is 3 - 6, show zoomed out
					layers[`${name}_17`].addLayer(marker);
				} else if (1 <= feature.PoiMapImportance && feature.PoiMapImportance <= 2) {
					layers[`${name}_18`].addLayer(marker);
				}
			}
		}

		if (zoom >= 15) {
			map.addLayer(layers[`${name}_15`]);
		}

		if (zoom >= 16) {
			map.addLayer(layers[`${name}_16`]);
		}

		if (zoom >= 17) {
			map.addLayer(layers[`${name}_17`]);
		}

		if (zoom >= 18) {
			map.addLayer(layers[`${name}_18`]);
		}

		// Hide and show layering based on zoom levels
		resetAllLayersOnImportance(name);
		resetIconsOnZoom(name, markers);
	}


	/**
	 * Create a Marker Cluster Group layer using a set of default properties;
	 * @param props The properties to pass into the marker cluster constructor.
	 */
	function createIconMarkerClusterGroup(props: Object): L.LayerGroup {
		const defaults = {
			showCoverageOnHover: false,
			spiderfyOnMaxZoom: false,
			zoomToBoundsOnClick: false
		};

		const properties = $.extend({}, defaults, props);

		return (L as any).markerClusterGroup(properties);
	}


	/**
	 * Adds the gate labels to a map layer.
	 */
	function renderGateLayer(): void {
		// Create a marker cluster group since elevators are common in group
		const layer = createIconMarkerClusterGroup({
			disableClusteringAtZoom: 19,
			iconCreateFunction(cluster) {
				return util.createGateClusterIcon(cluster);
			},
			maxClusterRadius: util.config.clusters.maxClusterRadius
		});

		for (const feature of cache.gates) {
			const latlng = L.latLng(feature.Latitude, feature.Longitude);
			const icon = util.createGateIcon(feature);
			const marker = L.marker(latlng, {
				// Use a custom `divIcon` so we remove all styles except for the text
				icon,
				interactive: false,
				keyboard: false
			});

			layer.addLayer(marker);
		}

		util.resetOneLayerOnImportance(map, layer, 17);
	}


	/**
	 * Adds the gate labels to a map layer.
	 */
	function renderGateAreasLayer() {
		util.removeLayer(map, layers.gateAreas);

		const markers: L.Marker[] = [];

		for (const feature of cache.gateAreas) {
			const latlng = L.latLng(feature.Latitude, feature.Longitude);
			const icon = util.createGateAreaIcon(feature);

			markers.push(L.marker(latlng, {
				// Use a custom `divIcon` so we remove all styles except for the text
				icon,
				interactive: false,
				keyboard: false
			}));
		}

		layers.gateAreas = L.layerGroup(markers);
		map.addLayer(layers.gateAreas);
	}


	/**
	 * Adds the locations to a map layer.
	 */
	function renderFacilityMarkers(data: IItinerary) {
		util.removeLayer(map, layers.locations);
		const markers: L.Marker[] = [];

		// Lopo through each step
		data.ItinerarySteps.forEach((step, index) => {
			const feature = step.Facility;

			// Some steps may not be matched to a facility.
			if (feature) {
				const latlng = L.latLng(feature.Latitude, feature.Longitude);

				// Remove the facility ID so the step won't link to pages.
				feature.FacilityId = null;

				// Create a popup with our location data from the gateway.
				const html = createPopupContent(step);
				const popup = L.popup(config.popup).setContent(html);

				const marker = L.marker(latlng, {
						icon: util.createStepIcon(step, parseInt(state.level, 10), index + 1)
					})
					.bindPopup(popup)
					.on('popupopen', (e: L.PopupEvent): void => {
						if (step.Facility && step.Facility.PoiBuildingLevel) {
							setFloor(step.Facility.PoiBuildingLevel);
						}
					});

				markers.push(marker);

				steps.push({
					index,
					marker,
					step
				});
			}
		});

		if (markers.length > 0) {
			layers.locations = L.layerGroup(markers);
			map.addLayer(layers.locations);

			markers[0].openPopup();
		}
	}


	//#endregion


	//#region POPUP CREATION AND EVENTS

	/**
	 * Creates the HTML content for a location popup.
	 * @param  {Object} step A dictionary of all location properties.
	 * @return {String}      HTML content for a location popup.
	 */
	function createPopupContent(step: IItineraryStep): string {
		// Run the feature properties through our Handlebars template.
		return templates.popup(step);
	}

	//#endregion


	//#region EVENT BINDINGS

	/**
	 * Changes the level shown on the map.
	 */
	function onLevelChange() {
		// Attach to the `baselayerchange` event.
		map.on('baselayerchange', e => {
			$('.yvr-map-label__name').text((e as any).name);

			for (const name in levels) {
				if (levels.hasOwnProperty(name) && levels[name] === (e as any).name) {
					state.level = name;
				}
			}

			steps.forEach((step) => {
				const icon = util.createStepIcon(step.step, parseInt(state.level, 10), step.index + 1);
				step.marker.setIcon(icon);
			});

			updateMap();

			gtm.eventToDataLayer(ANALYTICS_CATEGORY, 'filter|level', (e as any).name);
		});
	}


	/**
	 * Changes the room styles depending on the zoom level.
	 */
	function onZoomChange() {
		map.on('zoomend', () => {
			util.resetRoomStyle(map, state.terminals, config);
		});
	}

	//#endregion


	// Kick it
	init();
}
